import { useEffect, useState } from 'react';

import { Loader } from '@bitstopco/bitstop-theme';
import type { ModalChildrenProps } from '@bitstopco/bitstop-theme';

import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { Box, Button, DialogActions, DialogContent, Divider, Stack } from '@mui/material';
import type { ReactNode } from 'react';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';

import { useGetTransactionRedemptionQuery } from '@/store/api/services/redemptions';
import {
  useGetTransactionImagesQuery,
  useGetTransactionQuery,
} from '@/store/api/services/transactions/transactions';

import {
  adaptLegacyRedemption,
  deriveRedemptionState,
  getRedemptionBadgeColor,
} from '@/helpers/redemption';
import usePathInfo from '@/hooks/usePathInfo';

import TransactionTabs from '@/components/transaction/tabs/TransactionTabs';
import TransactionTabsPanels from '@/components/transaction/tabs/TransactionTabsPanels';
import {
  DISPENSE_PANEL,
  DISPENSE_PANEL_ROUTE,
  PANELS,
  PANELS_ROUTES,
  REDEMPTION_PANEL,
  REDEMPTION_PANEL_ROUTE,
} from '@/constants/app/transaction';
import type { TransactionImageSource } from '@/types';
import { TabContext } from '@mui/lab';

const BADGE_COLOR_HEX: Record<'red' | 'amber' | 'green', string> = {
  red: '#D32F2F',
  amber: '#B45309',
  green: '#2E7D32',
};

const RedemptionBadge = ({ color }: { color: 'red' | 'amber' | 'green' }) => (
  <Box
    component="span"
    data-test="redemption-badge"
    data-state={color}
    sx={{
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: 16,
      height: 16,
      borderRadius: '50%',
      fontSize: 11,
      lineHeight: 1,
      fontWeight: 700,
      color: '#fff',
      backgroundColor: BADGE_COLOR_HEX[color],
    }}
  >
    {color === 'green' ? '✓' : '!'}
  </Box>
);

// The single sale view's route for each drawer tab, so Open Full View lands on the same tab.
const FULL_VIEW_SUFFIX: Record<string, string> = {
  [PANELS_ROUTES[1]]: '/status-log',
  [DISPENSE_PANEL_ROUTE]: '/dispense',
  [REDEMPTION_PANEL_ROUTE]: '/redemption',
};

/**
 * Transaction details Modal
 *
 * @param {ModalChildrenProps} { hide, props }
 * @return {*}
 */
const TransactionDetailsMachine = ({ hide, props }: ModalChildrenProps) => {
  /**
   * Get the current pathname without the sale ID
   */
  const { pathWithoutParam } = usePathInfo();
  const navigate = useNavigate();

  /**
   * Get the current Sale ID
   */
  const saleId = useState(props?.data?.id)[0];

  const hideUrlParams = useState(!!props?.hideUrlParams)[0];

  /**
   * If hideAtmValue is true we will need to hide the atm column (if the modal was shown from the machine view)
   */
  const hideAtmValue = useState(props?.data?.hideAtmValue)[0];
  const hideCustomerLink = useState(props?.data?.hideCustomerLink)[0];

  const [activeTab, setActiveTab] = useState(PANELS_ROUTES[0]);

  /**
   * Get the Transactions data (this is a standalone modal)
   */
  const {
    data: { data, included } = {},
    isLoading,
    error,
  } = useGetTransactionQuery(saleId);
  const { data: imagesData, isLoading: isLoadingImages } = useGetTransactionImagesQuery(saleId);
  const tags = included?.tags || [];

  const images: TransactionImageSource[] = imagesData?.data || [];

  const hasDispenses = (data?.dispenses?.length ?? 0) > 0;

  // Falls back to the transaction's own embedded redemption field when the dedicated endpoint
  // doesn't return real obligation data -- see TransactionRedemptionTab for the full note.
  const { data: redemptionResponse } = useGetTransactionRedemptionQuery(saleId, {
    skip: !saleId || !hasDispenses,
  });

  useEffect(() => {
    if (error) {
      /**
       * If there is an error (loading the transaction data) we will show a toast notification and close this modal
       */
      toast.error('Transaction not found');
      hide();
    }
  }, [error]);

  useEffect(() => {
    if (!hideUrlParams) {
      /**
       * We need to clean the filtering values from the previous listing page
       */
      const query = new URLSearchParams(window.location.search);
      window.history.replaceState(null, '', `${pathWithoutParam}/${saleId}`);
      return () => {
        /**
         * If we unmount this modal we will want to put the URL back as before
         */
        window.history.replaceState(null, '', `${pathWithoutParam}?${query.toString()}`);
      };
    }
  }, []);

  const handleLocation = (tab: string) => {
    setActiveTab(tab);
  };

  const redemption = redemptionResponse?.data?.obligation
    ? redemptionResponse.data
    : adaptLegacyRedemption(data?.redemption, data?.rate);
  const redemptionState = deriveRedemptionState(data?.dispenses ?? [], redemption);
  const redemptionBadgeColor = getRedemptionBadgeColor(redemptionState);

  const redemptionPanelLabel: ReactNode = redemptionBadgeColor ? (
    <Stack direction="row" alignItems="center" spacing={0.75} component="span">
      <span>{REDEMPTION_PANEL}</span>
      <RedemptionBadge color={redemptionBadgeColor} />
    </Stack>
  ) : (
    REDEMPTION_PANEL
  );

  let panels: ReactNode[] = PANELS;
  let routes = PANELS_ROUTES;

  if (hasDispenses) {
    panels = [...panels, DISPENSE_PANEL, redemptionPanelLabel];
    routes = [...routes, DISPENSE_PANEL_ROUTE, REDEMPTION_PANEL_ROUTE];
  }

  return (
    <>
      <DialogContent
        sx={{ my: 2, maxWidth: 552, width: '100vw' }}
        data-test="transaction-details-modal"
      >
        {isLoading || isLoadingImages ? (
          <Loader />
        ) : (
          <>
            <Stack direction="row" justifyContent="flex-end" mb={1}>
              <Button
                onClick={() => {
                  hide();
                  navigate(`/sales/transactions/${saleId}${FULL_VIEW_SUFFIX[activeTab] ?? ''}`);
                }}
                size="small"
                endIcon={<OpenInNewIcon fontSize="small" />}
                data-test="transaction-open-full-view"
              >
                Open Full View
              </Button>
            </Stack>
            <TabContext value={activeTab}>
              <TransactionTabs panels={panels} routes={routes} onChange={handleLocation} />
              <TransactionTabsPanels
                transaction={data!}
                images={images}
                hideAtmValue={hideAtmValue}
                hideCustomerLink={hideCustomerLink}
                hide={hide}
                tagIds={data?.tag_ids ?? []}
                resourceId={data?.global_id ?? ''}
                tagsCollection={tags}
                panels={panels}
                routes={routes}
              />
            </TabContext>
          </>
        )}
      </DialogContent>
      <Divider />
      <DialogActions sx={{ justifyContent: 'flex-end' }}>
        <Button onClick={hide}>Back to sales</Button>
      </DialogActions>
    </>
  );
};

export default TransactionDetailsMachine;
